/**
 * Firebase Auth - Unified Auth Access
 * 
 * This module provides a proxy-based Auth accessor that handles initialization gracefully.
 * It works with firebase-loader.ts which uses synchronous imports for reliable auth access.
 */

import { 
  getAuthInstanceAsync, 
  getAuthInstance, 
  waitForFirebase, 
  getFirebaseModules, 
  isFirebaseInitialized 
} from './config';
import type { FirebaseUser } from '@/firebase/user';
import type { Auth as FirebaseAuth } from 'firebase/auth';

// Verbose logging helpers
const VERBOSE_LOGS = import.meta.env.VITE_FIREBASE_VERBOSE === 'true' || import.meta.env.DEV;
function verboseLog(message: string, data?: any) {
  if (VERBOSE_LOGS) {
    console.log(`[Firebase Auth] ${message}`, data !== undefined ? data : '');
  }
}
function verboseError(message: string, error?: any) {
  if (VERBOSE_LOGS) {
    console.error(`[Firebase Auth] ${message}`, error || '');
  }
}

/**
 * Export async getter for explicit async access
 */
export async function getAuthAsync(): Promise<FirebaseAuth> {
  return getAuthInstanceAsync();
}

/**
 * Auth proxy that handles initialization gracefully
 */
export const Auth = new Proxy({} as FirebaseAuth, {
  get(_target, prop) {
    // Check if Firebase is initialized
    if (isFirebaseInitialized()) {
      try {
        const authInstance = getAuthInstance();
        return (authInstance as any)[prop];
      } catch {
        // Fall through to special handling
      }
    }
    
    // currentUser returns null if not initialized
    if (prop === 'currentUser') {
      return null;
    }
    
    // onAuthStateChanged sets up listener after init
    if (prop === 'onAuthStateChanged') {
      return (callback: (user: any) => void) => {
        let unsubscribe: (() => void) | null = null;
        let isUnsubscribed = false;
        
        getAuthInstanceAsync().then((authInstance) => {
          if (isUnsubscribed) return;
          
          const modules = getFirebaseModules();
          if (modules.auth) {
            unsubscribe = modules.auth.onAuthStateChanged(authInstance, callback);
            verboseLog('onAuthStateChanged listener set up');
          } else {
            callback(null);
          }
        }).catch(err => {
          verboseError('Failed to set up onAuthStateChanged:', err);
          if (!isUnsubscribed) {
            callback(null);
          }
        });
        
        return () => {
          isUnsubscribed = true;
          if (unsubscribe) unsubscribe();
        };
      };
    }
    
    // signOut handles async
    if (prop === 'signOut') {
      return async () => {
        const authInstance = await getAuthInstanceAsync();
        const modules = getFirebaseModules();
        if (modules.auth) {
          return modules.auth.signOut(authInstance);
        }
        throw new Error('Auth module not available');
      };
    }
    
    verboseError(`Property '${String(prop)}' accessed before Auth is ready`);
    throw new Error(`Firebase Auth not ready. Property '${String(prop)}' cannot be accessed until initialization completes.`);
  },
  
  set(_target, prop, value) {
    if (isFirebaseInitialized()) {
      try {
        const authInstance = getAuthInstance();
        (authInstance as any)[prop] = value;
        return true;
      } catch {
        // Fall through
      }
    }
    return true;
  },
  
  has(_target, prop) {
    if (isFirebaseInitialized()) {
      try {
        const authInstance = getAuthInstance();
        return prop in (authInstance as any);
      } catch {
        // Fall through
      }
    }
    return ['currentUser', 'onAuthStateChanged', 'signOut', 'app'].includes(String(prop));
  }
});

/**
 * onAuthStateChanged helper - handles initialization internally
 */
export const onAuthStateChanged = (callback: (user: FirebaseUser | null) => void) => {
  let unsubscribe: (() => void) | null = null;
  let isUnsubscribed = false;
  
  getAuthInstanceAsync().then((authInstance) => {
    if (isUnsubscribed) return;
    
    const modules = getFirebaseModules();
    if (modules.auth) {
      unsubscribe = modules.auth.onAuthStateChanged(authInstance, callback);
      verboseLog('onAuthStateChanged listener set up');
    } else {
      callback(null);
    }
  }).catch(err => {
    verboseError('Failed to initialize for onAuthStateChanged:', err);
    if (!isUnsubscribed) {
      callback(null);
    }
  });
  
  return () => {
    isUnsubscribed = true;
    if (unsubscribe) unsubscribe();
  };
};
